/**
 * Tipos del flujo interactivo por pasos (WS4 / S8) tal como los produce
 * `chatgpt-nodejs` (`conversation/note-flow/*`). Solo las conversaciones
 * `flowVersion = 2` usan este flujo; las viejas siguen el chat legacy.
 *
 * Espejo (parcial, lo que la UI necesita) de:
 *  - `note-flow/info-request.ts`  → metadata del `info_request` (modal),
 *  - `note-flow/note-flow.service.ts#deliverNote` → metadata del `note`,
 *  - `note-flow/flow-state.ts`    → estados de la máquina.
 */

/** Estados de la máquina de estados de la nota (columna `flowState`). */
export type FlowState =
	| 'collecting'
	| 'awaiting_info'
	| 'generating'
	| 'reviewing'
	| 'awaiting_review_info'
	| 'pending_review'
	| 'completed'
	/**
	 * El ciclo generador↔auditor se murió y no va a entregar nada (B2-E). Antes
	 * el backend no tenía a dónde escribirlo y la fila se quedaba en
	 * `generating` para siempre: el chat enseñaba el spinner hasta que su
	 * vigilante cortaba a los 5 minutos. Se sale de aquí con «Regenerar»
	 * (intención `restart`), que vuelve a `collecting`.
	 */
	| 'failed';

/**
 * Idioma de la SESIÓN tal como lo persiste el backend en `sessionData.language`
 * (`'English' | 'Spanish'`). Deliberadamente distinto del enum `Languages`
 * (`'en' | 'es'`) que usa la UI: son dos vocabularios reales del sistema y
 * mezclarlos ya causó bugs. La conversión se hace en el borde, no se asume.
 */
export type SessionLanguage = 'English' | 'Spanish';

/**
 * Tipo de mensaje del flujo (columna `kind` en `MessageHistory`).
 *
 * Los cuatro primeros son valores que el backend PERSISTE. `cancel`,
 * `set_language` y `revalidate` solo viajan en el POST como acciones dedicadas —
 * el backend los despacha y guarda el mensaje como `text`, así que nunca vuelven
 * en un `MessageHistory`.
 */
export type MessageKind =
	| 'text'
	| 'info_request'
	| 'info_response'
	| 'note'
	| 'cancel'
	| 'set_language'
	| 'revalidate';

/** Tipo de control que el modal renderiza por campo. */
export type FlowFieldType =
	| 'text'
	| 'textarea'
	| 'time'
	| 'date'
	| 'select'
	| 'multiselect'
	| 'boolean';

/** Opción de un `select`/`multiselect` — `value` = id de catálogo, `label` visible. */
export interface InfoRequestOption {
	value: string;
	label: string;
}

/** Un campo del formulario dinámico del `info_request`. */
export interface InfoRequestField {
	key: string;
	label: string;
	type: FlowFieldType;
	options?: InfoRequestOption[];
	required: boolean;
	/** F5d punto 8: texto de ayuda dentro del campo, separado del label corto. */
	placeholder?: string;
	/**
	 * Límite de caracteres, cuando el backend lo fija por campo. Los dominios
	 * clínicos que se piden en prosa (paciente sin catálogo) son narrativa real y
	 * no caben en el defecto de 200 de una confirmación puntual.
	 */
	maxLength?: number;
	/**
	 * Valor inicial (hoy solo `multiselect`: ids ya seleccionados). Lo mandan los
	 * campos de dominio de catálogo, cuyo merge REEMPLAZA el array completo: sin
	 * premarcar lo ya elegido, enviar el formulario borraría el resto.
	 */
	defaultValue?: string[];
	/**
	 * Explicación completa de qué se pide, en su propio bloque encima del control.
	 * `label` y `placeholder` no pueden mostrarla: el label de Material es
	 * `nowrap` + elipsis y el hint reserva una sola línea.
	 */
	explanation?: string;
}

/** `metadata` de un mensaje `kind = info_request` (spec del formulario del modal). */
export interface InfoRequestMetadata {
	kind: 'info_request';
	fields: InfoRequestField[];
	/** Hallazgos del auditor que motivaron el formulario, en sus palabras. */
	context?: string[];
}

/** Advertencia del auditor adjunta a la nota entregada (auditor agotado). */
export interface NoteWarning {
	rule: string;
	problem: string;
}

/** `metadata` de un mensaje `kind = note` (nota bilingüe en revisión o firmada). */
export interface NoteMetadata {
	kind: 'note';
	// `pending_review` = borrador editable; `signed` = firmada e inmutable (S9).
	status: 'pending_review' | 'signed';
	en: string;
	es: string;
	warnings: NoteWarning[];
	gateViolations: unknown[];
	// Firma (WS4 paso 6 / S9). Presentes solo cuando `status = 'signed'`. `en`/`es`
	// ya incluyen el bloque de firma. `signedRole` es el rol ABA de la sesión.
	signedBy?: string;
	signedRole?: string;
	signedAt?: string;
}

/** Payload de la firma (`POST /conversation/:id/sign`). */
export interface SignNotePayload {
	en: string;
	es: string;
	attested: true;
	language?: 'en' | 'es';
}

/** Type guard: ¿la metadata corresponde a un `info_request`? */
export function isInfoRequestMetadata(
	metadata: unknown
): metadata is InfoRequestMetadata {
	return (
		!!metadata &&
		typeof metadata === 'object' &&
		(metadata as { kind?: unknown }).kind === 'info_request' &&
		Array.isArray((metadata as { fields?: unknown }).fields)
	);
}

/** Type guard: ¿la metadata corresponde a una `note`? */
export function isNoteMetadata(metadata: unknown): metadata is NoteMetadata {
	return (
		!!metadata &&
		typeof metadata === 'object' &&
		(metadata as { kind?: unknown }).kind === 'note'
	);
}
